import type { WorkbookDefinition } from '@erp/shared-types';
import type { ToolCall } from '@/context/agent-state.context';

/** At or above this P(match), regenerating an unchanged Instruction asks first. */
export const LIKELY_MATCH = 0.95;

type ProcessStep = WorkbookDefinition['processSteps'][number];

/**
 * Applied: generated (it has Linked items) and not up for review, so Generation would
 * regenerate an Instruction that has not changed since it was last applied.
 */
export function isStepApplied(step: ProcessStep): boolean {
  return step.itemIds.length > 0 && !step.reviewStatus;
}

/** What Generation is doing now, in the order the agent does it. */
export type GenerationActivity =
  | 'checking'
  | 'thinking'
  | 'reading'
  | 'reading-file'
  | 'finding-data'
  | 'previewing'
  | 'trying'
  | 'applying'
  | 'fixing'
  | 'running'
  | 'summarizing'
  | { tool: ToolCall };

/** An apply the agent must follow up: it failed, or saved with Diagnostics. */
function reportedProblems(call: ToolCall): boolean {
  if (call.status === 'error') return true;
  const result: unknown = call.result;
  if (!result || typeof result !== 'object') return false;
  if ('ok' in result && result.ok === false) return true;
  return hasDiagnostics('data' in result ? result.data : result);
}

function hasDiagnostics(data: unknown): boolean {
  if (!data || typeof data !== 'object' || !('diagnostics' in data))
    return false;
  return Array.isArray(data.diagnostics) && data.diagnostics.length > 0;
}

const isApply = (toolName: string) =>
  toolName === 'apply_workbook' || toolName === 'edit_workbook';

export function generationActivity(
  toolCalls: readonly ToolCall[],
  streamingText: string,
  checking: boolean,
): GenerationActivity {
  if (checking) return 'checking';
  const current = toolCalls.at(-1);
  if (!current) return 'thinking';
  if (current.status !== 'pending' && streamingText) return 'summarizing';
  switch (current.toolName) {
    case 'get_workbook':
      return 'reading';
    case 'inspect_workbook_file':
      return 'reading-file';
    case 'workbook_catalog':
    case 'workbook_object_catalog':
    case 'profile_workbook_data':
      return 'finding-data';
    case 'preview_workbook_source':
      return 'previewing';
    case 'preview_workbook_change':
      return 'trying';
    case 'apply_workbook':
    case 'edit_workbook':
      return toolCalls
        .slice(0, -1)
        .some((call) => isApply(call.toolName) && reportedProblems(call))
        ? 'fixing'
        : 'applying';
    case 'run_open_workbook':
    case 'workbook_runs':
      return 'running';
    default:
      return { tool: current };
  }
}

/**
 * Whether the agent is working on this workbook rather than on something else
 * the user asked in the chat (like opening another page): the AI check runs,
 * or a turn is streaming that this workbook asked for or that has called one
 * of its tools.
 */
export function workingOnWorkbook({
  checking,
  agentBusy,
  askedHere,
  toolCalls,
  workbookTools,
  ready,
}: {
  checking: boolean;
  agentBusy: boolean;
  askedHere: boolean;
  toolCalls: readonly { toolName: string }[];
  /** The page's registered tools; this workbook's only when `ready`. */
  workbookTools: readonly { name: string }[] | undefined;
  ready: boolean;
}): boolean {
  if (checking) return true;
  if (!agentBusy) return false;
  if (askedHere) return true;
  const tools = ready ? (workbookTools ?? []) : [];
  return toolCalls.some((call) =>
    tools.some((tool) => tool.name === call.toolName),
  );
}
